import { useState } from 'react';
import { Copy, Check, ExternalLink, FileText, AlertTriangle, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';

interface WidgetCodeGeneratorProps {
  slug: string;
}

const WIDTH_OPTIONS = [
  { value: '100%', label: '100% (responsive)' },
  { value: '400px', label: '400px' },
  { value: '500px', label: '500px' },
  { value: '600px', label: '600px' },
];

const HEIGHT_OPTIONS = [
  { value: '600', label: '600px' },
  { value: '700', label: '700px (recommandé)' },
  { value: '800', label: '800px' },
];

export default function WidgetCodeGenerator({ slug }: WidgetCodeGeneratorProps) {
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('700');
  const [copied, setCopied] = useState(false);

  const baseUrl = 'https://piktable.ch';
  const embedUrl = `${baseUrl}/${slug}/embed`;

  const iframeCode = `<iframe 
  src="${embedUrl}" 
  width="${width}" 
  height="${height}" 
  frameborder="0"
  style="border-radius: 12px; max-width: 500px; border: 1px solid #e5e7eb;">
</iframe>`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(iframeCode).then(() => {
      setCopied(true);
      toast({
        title: 'Code copié !',
        description: 'Le code du widget a été copié dans le presse-papier.',
      });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-4">
      {/* Options */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="width" className="text-sm">Largeur</Label>
          <Select value={width} onValueChange={setWidth}>
            <SelectTrigger id="width">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WIDTH_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="height" className="text-sm">Hauteur</Label>
          <Select value={height} onValueChange={setHeight}>
            <SelectTrigger id="height">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HEIGHT_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Code preview */}
      <div className="space-y-2">
        <Label className="text-sm">Code à copier</Label>
        <Textarea
          readOnly
          value={iframeCode}
          className="font-mono text-xs min-h-[120px] bg-muted/50"
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleCopyCode} variant="default">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copié !' : 'Copier le code'}
        </Button>
        <Button variant="outline" asChild>
          <a href={embedUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" />
            Aperçu
          </a>
        </Button>
      </div>

      {/* Avertissement navigation privée */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          <strong>Note :</strong> Si vous êtes connecté à votre dashboard, vous serez automatiquement redirigé. 
          Utilisez une <strong>navigation privée</strong> (Ctrl+Shift+N ou Cmd+Shift+N) pour tester le lien comme vos clients le verront.
        </p>
      </div>

      {/* Guide d'intégration complet */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" />
          <p className="font-medium text-sm">Guide d'intégration en 3 étapes</p>
        </div>
        
        {/* Étape 1 */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">1</span>
            <p className="font-medium text-sm">Créez une page sur votre site</p>
          </div>
          <p className="text-xs text-muted-foreground ml-7">
            Créez une nouvelle page dédiée aux réservations sur votre site web 
            (par exemple : <code className="bg-muted px-1 rounded">votresite.ch/reservation</code> ou 
            <code className="bg-muted px-1 rounded ml-1">votresite.ch/book</code>).
          </p>
        </div>
        
        {/* Étape 2 */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">2</span>
            <p className="font-medium text-sm">Intégrez le widget</p>
          </div>
          <p className="text-xs text-muted-foreground ml-7">
            Copiez le code iframe ci-dessus et collez-le dans le code HTML de cette page.
            Le formulaire de réservation apparaîtra automatiquement.
          </p>
        </div>

        {/* Alternative - Bouton de redirection */}
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <MousePointerClick className="w-4 h-4 text-green-600 dark:text-green-400" />
            <p className="font-medium text-sm text-green-800 dark:text-green-200">Alternative : Utilisez un simple bouton</p>
          </div>
          <p className="text-xs text-green-700 dark:text-green-300 ml-6">
            Vous pouvez également créer un bouton "Réserver" sur votre site qui redirige directement vos clients vers votre page de réservation :
          </p>
          <div className="ml-6 p-2 bg-green-100 dark:bg-green-900/50 rounded font-mono text-xs text-green-800 dark:text-green-200 overflow-x-auto">
            &lt;a href="{embedUrl}"&gt;Réserver une table&lt;/a&gt;
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 ml-6 italic">
            💡 Cette méthode est plus simple si vous ne souhaitez pas intégrer le formulaire directement dans votre site.
          </p>
        </div>
        
        {/* Étape 3 - Google Business */}
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">3</span>
            <p className="font-medium text-sm text-blue-800 dark:text-blue-200">Ajoutez le lien sur Google Business (optionnel)</p>
          </div>
          <p className="text-xs text-blue-700 dark:text-blue-300 ml-7">
            Pour permettre aux clients de réserver directement depuis votre fiche Google :
          </p>
          <ol className="text-xs text-blue-700 dark:text-blue-300 ml-7 list-decimal list-inside space-y-1">
            <li>Connectez-vous à votre compte Google Business</li>
            <li>Allez dans <strong>"Modifier le profil"</strong> → <strong>"Coordonnées"</strong></li>
            <li>Dans le champ <strong>"Lien de réservation"</strong>, entrez l'URL de votre page 
              (ex: <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">votresite.ch/reservation</code>)</li>
            <li>Enregistrez les modifications</li>
          </ol>
          <p className="text-xs text-blue-600 dark:text-blue-400 ml-7 mt-2 italic">
            💡 Vos clients pourront alors cliquer sur "Réserver" directement depuis Google Maps et votre fiche Google.
          </p>
        </div>
      </div>
    </div>
  );
}
